import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useAuth } from '../context/AuthContext';
import { EventFormData } from '../models';
import { API_BASE_URL } from '../config';
import { utcToETInputValue, etInputValueToUTC } from '../utils/dateUtils';

const EventForm: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    event_type: 'concert',
    event_start_date: '',
    event_end_date: '',
    event_address: '',
    description: '',
    content: '',
    event_ticket_price_adults: 0,
    event_ticket_price_kids: 0,
    discount_allowed: false,
    is_disabled: false,
    image_file: null,
    image_preview: '',
    video_urls: ''
  });

  const fetchEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/events/${uuid}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch event');

      const data = await response.json();
      const event = data.event;

      // Format dates for datetime-local input — convert UTC DB value to ET wall-clock time
      setFormData({
        title: event.title,
        event_type: event.event_type,
        event_start_date: utcToETInputValue(event.event_start_date),
        event_end_date: utcToETInputValue(event.event_end_date),
        event_address: event.event_address,
        description: event.description,
        content: event.content || '',
        event_ticket_price_adults: event.event_ticket_price_adults,
        event_ticket_price_kids: event.event_ticket_price_kids,
        discount_allowed: event.discount_allowed,
        is_disabled: event.is_disabled,
        image_file: null,
        image_preview: event.image_files || '',
        video_urls: event.video_urls || ''
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }

    // If editing, fetch event data
    if (uuid) {
      fetchEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, user, navigate]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const files = (e.target as HTMLInputElement).files;
    
    if (name === 'image_file' && files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image_file: file,
          image_preview: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }
  };

  const handleContentChange = (value: string) => {
    setFormData(prev => ({ ...prev, content: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const url = uuid 
        ? `${API_BASE_URL}/events/${uuid}`
        : `${API_BASE_URL}/events`;
      
      const method = uuid ? 'PUT' : 'POST';

      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('event_type', formData.event_type);
      submitData.append('event_start_date', etInputValueToUTC(formData.event_start_date));
      submitData.append('event_end_date', etInputValueToUTC(formData.event_end_date));
      submitData.append('event_address', formData.event_address);
      submitData.append('description', formData.description);
      submitData.append('content', formData.content);
      submitData.append('event_ticket_price_adults', formData.event_ticket_price_adults.toString());
      submitData.append('event_ticket_price_kids', formData.event_ticket_price_kids.toString());
      submitData.append('discount_allowed', formData.discount_allowed.toString());
      submitData.append('video_urls', formData.video_urls);
      
      // Add image file if selected
      if (formData.image_file) {
        submitData.append('image', formData.image_file);
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: submitData
      });

      const data = await response.json();
      

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save event');
      }

      setSuccess(uuid ? 'Event updated successfully!' : 'Event created successfully!');
      setTimeout(() => {
        navigate('/events');
      }, 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-yellow-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-elegant font-bold text-secondary-900 mb-6">
            {uuid ? 'Edit Event' : 'Create New Event'}
          </h1>

          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded-lg mb-4">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                Event Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
                required
              />
            </div>

            {/* Event Type */}
            <div>
              <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                Event Type *
              </label>
              <select
                name="event_type"
                value={formData.event_type}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
              >
                <option value="concert">Concert</option>
                <option value="contest">Contest</option>
              </select>
            </div>

            {/* Date and Time Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                  Start Date & Time *
                </label>
                <input
                  type="datetime-local"
                  name="event_start_date"
                  value={formData.event_start_date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
                  required
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                  End Date & Time *
                </label>
                <input
                  type="datetime-local"
                  name="event_end_date"
                  value={formData.event_end_date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
                  required
                />
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                Address *
              </label>
              <textarea
                name="event_address"
                value={formData.event_address}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
                required
              />
            </div>

            {/* Event Description */}
            <div>
              <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                Event Description (Short) *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
                required
              />
            </div>

            {/* Rich Text Content */}
            <div>
              <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                Detailed Content (WYSIWYG)
              </label>
              <div className="bg-white">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={handleContentChange}
                  modules={quillModules}
                  className="h-64 mb-12"
                />
              </div>
            </div>

            {/* Pricing Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                  Adults Price ($) *
                </label>
                <input
                  type="number"
                  name="event_ticket_price_adults"
                  value={formData.event_ticket_price_adults}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
                  required
                />
              </div>
              <div>
                <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                  Kids Price ($) *
                </label>
                <input
                  type="number"
                  name="event_ticket_price_kids"
                  value={formData.event_ticket_price_kids}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
                  required
                />
              </div>
            </div>

            {/* Discount Allowed */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="discount_allowed"
                checked={formData.discount_allowed}
                onChange={handleInputChange}
                className="w-5 h-5 text-primary-600 border-2 border-secondary-300 rounded focus:ring-primary-500 cursor-pointer"
              />
              <label className="ml-3 text-base font-semibold text-primary-700 cursor-pointer">
                Discount Allowed
              </label>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                Event Image
              </label>
              <input
                type="file"
                name="image_file"
                accept="image/*"
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-primary-700 font-medium cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
              {formData.image_preview && (
                <div className="mt-4">
                  <img
                    src={formData.image_preview}
                    alt="Event Preview"
                    className="rounded-lg shadow-md max-h-48 w-auto border-2 border-primary-200"
                  />
                </div>
              )}
            </div>

            {/* Video URLs */}
            <div>
              <label className="block text-base font-semibold text-primary-700 mb-2 tracking-wide">
                Video URLs (comma-separated)
              </label>
              <input
                type="text"
                name="video_urls"
                value={formData.video_urls}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
                placeholder="https://youtube.com/watch?v=..., https://vimeo.com/..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/events')}
                className="px-6 py-3 border border-secondary-300 text-secondary-700 rounded-lg hover:bg-secondary-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 via-primary-700 to-warm-700 text-white rounded-lg hover:from-primary-700 hover:via-primary-800 hover:to-warm-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : uuid ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventForm;
